import { Action, State, StateContext, Store } from "@ngxs/store";
import { Injectable } from "@angular/core";
import { ServiceConceptType } from "../../interfaces/serviceConceptType";
import { ServiceConceptTypeActions } from './service-concepts-type.actions';
import { ServiceConceptsTypeService } from '../../services/service-concepts-type.service';
import { AlertService } from "src/app/shared/store/alert/alert.service";
import { LocalizationService } from "src/app/services/localization/localization.service";
import { InvoiceType } from "../../interfaces/invoiceType";
import { MassiveActions } from "src/app/modules/massive-actions/store/massive/massive.actions";
import { MappingUtilsService } from "src/app/services/utils/mapping-utils.service";

export class ServiceConceptTypeStateModel {
  serviceConceptsTypes: ServiceConceptType[];
  invoiceTypes: InvoiceType[];
  serviceConceptTypeForm: {
    model: ServiceConceptType
  };
  filters: {
    model: ServiceConceptType
  };
  public isLoading: boolean;
  public isListSmaller: boolean;
  public error: string;
}

const defaults = {
  serviceConceptsTypes: [],
  invoiceTypes: [],
  serviceConceptTypeForm: {
    model: null
  },
  filters: {
    model: null
  },
  isLoading: false,
  isListSmaller: true,
  error: null
}

@State<ServiceConceptTypeStateModel>({
  name: "serviceConceptsTypes",
  defaults
})
@Injectable()
export class ServiceConceptTypeState {
  constructor(
    private serviceConceptsTypeService: ServiceConceptsTypeService,
    private alertService: AlertService,
    private localizationService: LocalizationService,
    public store: Store,
  ) {}

  @Action(ServiceConceptTypeActions.GetServiceConceptsType)
  getServiceConcepts(ctx: StateContext<ServiceConceptTypeStateModel>, { }: ServiceConceptTypeActions.GetServiceConceptsType) {
    ctx.patchState({isLoading: true});
    return this.serviceConceptsTypeService.getServiceConceptsType().subscribe({
      next: (res) =>
        ctx.dispatch(new ServiceConceptTypeActions.GetServiceConceptsTypeSuccess(res)),
      error: (error) => {
        ctx.dispatch(new ServiceConceptTypeActions.Error(error, ''));
      }
    });
  }

  @Action(ServiceConceptTypeActions.GetServiceConceptsTypeFiltered)
  getServiceConceptsFiltered(ctx: StateContext<ServiceConceptTypeStateModel>, { name, isFixed, isMeal, isActive }: ServiceConceptTypeActions.GetServiceConceptsTypeFiltered) {
    ctx.patchState({isLoading: true});
    return this.serviceConceptsTypeService.getServiceConceptsTypeFiltered(name, isFixed, isMeal, isActive).subscribe({
      next: (res) =>
        ctx.dispatch(new ServiceConceptTypeActions.GetServiceConceptsTypeSuccess(res)),
      error: (error) => {
        ctx.dispatch(new ServiceConceptTypeActions.Error(error, ''));
      }
    });
  }

  @Action(ServiceConceptTypeActions.GetServiceConceptsTypeSuccess)
  getServiceConceptsSuccess(ctx: StateContext<ServiceConceptTypeStateModel>, { serviceConceptsTypes }: ServiceConceptTypeActions.GetServiceConceptsTypeSuccess) {
    ctx.patchState({
      serviceConceptsTypes: serviceConceptsTypes.map(item => {
        return {...item, isMeal: item.isFoodService}
      }),
      isLoading: false,
    });
  }


  @Action(ServiceConceptTypeActions.GetInvoiceTypes)
  getInvoiceTypes(ctx: StateContext<ServiceConceptTypeStateModel>, { }: ServiceConceptTypeActions.GetInvoiceTypes) {
    ctx.patchState({isLoading: true});
    return this.serviceConceptsTypeService.getInvoiceTypes().subscribe({
      next: (res) =>
        ctx.dispatch(new ServiceConceptTypeActions.GetInvoiceTypesSuccess(res)),
      error: (error) => {
        ctx.dispatch(new ServiceConceptTypeActions.Error(error, ''));
      }
    });
  }

  @Action(ServiceConceptTypeActions.GetInvoiceTypesSuccess)
  getInvoiceTypesSuccess(ctx: StateContext<ServiceConceptTypeStateModel>, { invoiceTypes }: ServiceConceptTypeActions.GetInvoiceTypesSuccess) {
    ctx.patchState({
      invoiceTypes: invoiceTypes,
      isLoading: false,
    });
  }

  @Action(ServiceConceptTypeActions.AddServiceConceptsType)
  addServiceConcept(ctx: StateContext<ServiceConceptTypeStateModel>, {serviceConceptType}: ServiceConceptTypeActions.AddServiceConceptsType) {
    this.alertService.confirm(
    this.localizationService.translate("labels.service-concept-type"),
    this.localizationService.translate('masterForms.serviceConceptAddConceptConfirm'),
    this.localizationService.translate('buttons.accept'),
    this.localizationService.translate('buttons.cancel'),
    this.localizationService.translate('masterForms.serviceConceptAddConceptConfirm')
    .replace('{0}', serviceConceptType.serviceConceptTypeName)
    ).then((result) => {
      if (result.value) {
        ctx.patchState({isLoading: true});
        let request = {
          name: serviceConceptType.serviceConceptTypeName,
          order: serviceConceptType.order,
          isFixed: serviceConceptType.isFixed,
          InvoiceAccountId: serviceConceptType.invoiceAccountId,
          IsFoodType: serviceConceptType.isMeal
        }
        return this.serviceConceptsTypeService.createServiceConcept(request).subscribe({
          next: (res) => {
            ctx.dispatch(new ServiceConceptTypeActions.AddServiceConceptsTypeSuccess(serviceConceptType))
          },
          error: (error) => {
            ctx.dispatch(new ServiceConceptTypeActions.Error(error, serviceConceptType.serviceConceptTypeName));
          }
        });
      } else {
        return null;
      }
    });
  }

  @Action(ServiceConceptTypeActions.AddServiceConceptsTypeSuccess)
  addServiceConceptSuccess(ctx: StateContext<ServiceConceptTypeStateModel>, {serviceConceptType}: ServiceConceptTypeActions.AddServiceConceptsTypeSuccess) {
    ctx.patchState({
      serviceConceptsTypes: [...ctx.getState().serviceConceptsTypes, serviceConceptType],
      isLoading: false,
    });
  }

  @Action(ServiceConceptTypeActions.EditServiceConceptsType)
  editServiceConcept(ctx: StateContext<ServiceConceptTypeStateModel>, {connectionId, channel}: ServiceConceptTypeActions.EditServiceConceptsType) {
    const state = ctx.getState();
    const serviceConceptsEdited = state.serviceConceptsTypes.filter(l => l.edited === true)
    const editedRows = serviceConceptsEdited.length;
    this.alertService.confirm(
      this.localizationService.translate("labels.service-concept-type"),
      this.localizationService.translate('masterForms.serviceConceptEditConfirm'),
      this.localizationService.translate('buttons.accept'),
      this.localizationService.translate('buttons.cancel'),
      this.localizationService.translate('masterForms.serviceConceptEditConfirm')
      .replace('{0}', editedRows.toString())
    ).then((result) => {
      if (result.value) {
        ctx.patchState({isLoading: true});
        let request = {
          connectionId: connectionId,
          channel: channel,
          updateServiceConceptTypeRequestItems: []
        }
        request.updateServiceConceptTypeRequestItems = serviceConceptsEdited.map(item => {
          return {
            serviceConceptTypeId: item.serviceConceptTypeId,
            name: item.serviceConceptTypeName,
            order: item.order,
            isFixed: item.isFixed,
            invoiceAccountId: item.invoiceAccountId,
            isFoodType: item.isMeal,
            isActive: item.isActive
          }
        });
        return this.serviceConceptsTypeService.updateServiceConcept(request).subscribe({
          next: (res) => ctx.dispatch(new ServiceConceptTypeActions.EditServiceConceptsTypeSuccess(res)),
          error: (error) => {
            ctx.dispatch(new ServiceConceptTypeActions.Error(error, ""));
          }
        });
      } else {
        return null;
      }
    });
  }

  @Action(ServiceConceptTypeActions.EditServiceConceptsTypeSuccess)
  editServiceConceptSuccess(
    ctx: StateContext<ServiceConceptTypeStateModel>,
    {massiveResponse}: ServiceConceptTypeActions.EditServiceConceptsTypeSuccess
  ) {
    const state = ctx.getState();
    const serviceConcepts = state.serviceConceptsTypes.map(item => {
      return {...item, edited: false}
    });
    this.store.dispatch(new MassiveActions.SetMassiveResponse(massiveResponse))
    ctx.patchState({
      ...state, serviceConceptsTypes: serviceConcepts,
      isLoading: false,
    });
  }

  @Action(ServiceConceptTypeActions.ToggleAllSelected)
  toggleAllSelected(ctx: StateContext<ServiceConceptTypeStateModel>, {column, selected}: ServiceConceptTypeActions.ToggleAllSelected) {
    const state = ctx.getState();
    let updatedServiceConcepts;
    switch (column) {
      case 'fixed':
        updatedServiceConcepts = state.serviceConceptsTypes.map(elem => ({...elem, isFixed: selected, edited: true}));
        break;
      case 'meal':
        updatedServiceConcepts = state.serviceConceptsTypes.map(elem => ({...elem, isMeal: selected, edited: true}));
        break;
      case 'active':
        updatedServiceConcepts = state.serviceConceptsTypes.map(elem => ({...elem, isActive: selected, edited: true}));
        break;
    }
    ctx.patchState({...state, serviceConceptsTypes: updatedServiceConcepts});
  }

  @Action(ServiceConceptTypeActions.EditTableCell)
  editTableCell(ctx: StateContext<ServiceConceptTypeStateModel>, {rowId, fieldName, newValue}: ServiceConceptTypeActions.EditTableCell) {
    const state = ctx.getState();
    const updatedData = state.serviceConceptsTypes.map(row => {
      if(row.serviceConceptTypeId === rowId && row[fieldName] != newValue) {
        return {...row, [fieldName]: newValue, edited: true};
      }
      return row;
    });
    ctx.patchState({
      serviceConceptsTypes: updatedData
    });
  }

  @Action(ServiceConceptTypeActions.SetDefaultState)
  setDefaultState(
    ctx: StateContext<ServiceConceptTypeStateModel>,
    {serviceConceptTypeDefaults}: ServiceConceptTypeActions.SetDefaultState
  ) {
    const state = ctx.getState();
    ctx.patchState({
      ...state, serviceConceptTypeForm: {model: serviceConceptTypeDefaults}
    });
  }

 @Action(ServiceConceptTypeActions.SetDefaults)
  setDefaults(
    ctx: StateContext<ServiceConceptTypeStateModel>,
    {}: ServiceConceptTypeActions.SetDefaults
  ) {
    ctx.patchState(
      defaults
    );
  }

  @Action(ServiceConceptTypeActions.Error)
  onErrorOcurred(
    ctx: StateContext<ServiceConceptTypeStateModel>,
    {error, details}: ServiceConceptTypeActions.Error
  ) {
    ctx.patchState({
      error: error,
      isLoading: false,
    });
    MappingUtilsService.showError(this.getTitle(), error, this.alertService, this.localizationService);
  }

  @Action(ServiceConceptTypeActions.SetIsListSmaller)
  setIsListSmaller(ctx: StateContext<ServiceConceptTypeStateModel>, {isListSmaller}: ServiceConceptTypeActions.SetIsListSmaller) {
    ctx.patchState({
      isListSmaller: isListSmaller
    });
  }

  private getTitle() {
    return this.localizationService.translate("labels.service-concept-type");
  }
}
